import { strictify } from '~/utils/strictify'
import * as operations from '../../generated'
import type { z } from 'zod'
import { extendSchemaField } from '~/utils/extendSchemaField'
import { unboundedValueCodec } from '~/utils/unboundedValueCodec'

const storeMenusCustomizationLimitExtension = {
  max_per_option: unboundedValueCodec('Infinity'),
  max_total_selections: unboundedValueCodec('Infinity')
}

type StoreMenusData = z.output<
  ReturnType<
    typeof extendSchemaField<
      typeof operations.StoreMenusSuccessSchema,
      readonly ['data', 'menus', 'categories', 'menu_items', 'customizations'],
      typeof storeMenusCustomizationLimitExtension
    >
  >
>['data']

export type PublicStoreMenus = StoreMenusData
export type PublicStoreMenu = StoreMenusData['menus'][number]
export type PublicStoreMenuCategory = PublicStoreMenu['categories'][number]
export type PublicStoreMenuItem = PublicStoreMenuCategory['menu_items'][number]
export type PublicStoreMenuCustomizationGroup = PublicStoreMenuItem['customizations'][number]

export function useMenusPublicService() {
  const apiBaseUrl = useRuntimeConfig().public.apiBaseUrl
  if (typeof apiBaseUrl !== 'string' || apiBaseUrl.length === 0) {
    throw new TypeError('Runtime configuration requires public.apiBaseUrl.')
  }
  const baseURL = `${apiBaseUrl}/api`

  async function getMenuOptions(): Promise<operations.MenuOptionsSuccess['data']> {
    const response = operations.MenuOptionsSuccessSchema.parse(
      await $fetch('/menus/options', { baseURL })
    )
    return structuredClone(response.data)
  }

  async function getStoreMenusBySlug(query: operations.StoreMenusQuery): Promise<StoreMenusData> {
    const request = operations.StoreMenusQuerySchema.strict().parse(structuredClone(query))
    const response = strictify(
      extendSchemaField(
        operations.StoreMenusSuccessSchema,
        ['data', 'menus', 'categories', 'menu_items', 'customizations'],
        storeMenusCustomizationLimitExtension
      )
    ).parse(await $fetch('/stores/menus', { baseURL, query: request }))
    return structuredClone(response.data)
  }

  return {
    getMenuOptions,
    getStoreMenusBySlug
  }
}
